import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { GUIDED_CONNECTION_WIDTHS } from '@hookdeck/theme';
import LINKS from '../../../../configs/links';
import Badge from '../../../common/base/Badge';
import Container from '../../../common/base/Container';
import Icon, { IconName } from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useGuidedStepScroll } from '../../../hooks/useGuidedStepScroll';
import useLocalStorage from '../../../hooks/useLocalStorage';
import FullscreenLayout from '../../../layouts/FullscreenLayout';
import { StyledProgress } from '../Connections/Create/CreateView';
import { DashboardContext } from '../DashboardContext';
import GuidedCLIOnboardingNav from './GuidedCLIOnboardingNav';
import ConnectCLI from './Steps/ConnectCLI';
import DefineWebhookSource from './Steps/DefineWebhookSource';
import InspectEvents from './Steps/InspectEvents';
import InstallHookdeckCLI from './Steps/InstallHookdeckCLI';
import LocalServerSetup from './Steps/LocalServerSetup';
import RunHookdeckLogin from './Steps/RunHookdeckLogin';
import SetupComplete from './Steps/SetupComplete';
import TriggerEvent from './Steps/TriggerEvent';
export interface CLIOnboardingContext {
  source: any | null;
  cli_connected: boolean;
  /** True once the user has completed the connect_cli step (localStorage), even if listen disconnects. */
  post_connect_unlocked: boolean;
  events_list: any | null;
  progress: number;
  selected_event_id: string | null;
  setSelectedEventId: (id: string | null) => void;
}

/** Reveal rule for trigger / inspect / complete (single source of truth for step visibility and UI such as warnings). */
export function revealGuidedCliOnboardingPostConnectSteps(context: CLIOnboardingContext): boolean {
  return !!context.source && (context.cli_connected || context.post_connect_unlocked);
}
const steps = [{
  key: 'source',
  nav_label: 'Define event source',
  label: 'Define your webhook source',
  description: 'Sources are Hookdeck endpoints that represent webhook producers. When you connect the CLI in the following steps, this source will be shown in your terminal.',
  icon: 'source' as const,
  Component: DefineWebhookSource,
  reference: {
    title: 'Sources',
    text: 'Sources represent the producer of your events. In Hookdeck, any service that sends HTTP requests can be defined as a source.',
    link: LINKS.product_docs.sources
  }
}, {
  key: 'local_server',
  nav_label: 'Connect Hookdeck CLI',
  label: 'Local server setup for receiving webhooks?',
  description: "If you don't have a local server setup for receiving webhooks, expand the accordion below for a how-to guide. If you do have one setup, you can move to the next step.",
  icon: 'help' as const,
  optional: true,
  Component: LocalServerSetup,
  reveal: (context: CLIOnboardingContext) => !!context.source
}, {
  key: 'install_cli',
  label: 'Install the Hookdeck CLI',
  description: 'Run the following command in your terminal to install the CLI:',
  icon: 'terminal' as const,
  Component: InstallHookdeckCLI,
  reveal: (context: CLIOnboardingContext) => !!context.source
}, {
  key: 'run_login',
  label: 'Authenticate the CLI',
  description: 'Run the command below in your terminal to authenticate the Hookdeck CLI.',
  icon: 'login' as const,
  Component: RunHookdeckLogin,
  reveal: (context: CLIOnboardingContext) => !!context.source
}, {
  key: 'connect_cli',
  label: 'Connect CLI to your local server',
  description: 'With your local server running, start the Hookdeck CLI to get a public URL representing the source you configured above. That source URL will be used as the webhook endpoint with your webhook provider, and to forward webhooks to your local server.',
  icon: 'connect' as const,
  Component: ConnectCLI,
  reveal: (context: CLIOnboardingContext) => !!context.source
}, {
  key: 'trigger',
  nav_label: 'Trigger an event',
  label: 'Trigger an event',
  description: 'Fire an event that will send webhooks to your Hookdeck source URL.',
  icon: 'send' as const,
  Component: TriggerEvent,
  reveal: revealGuidedCliOnboardingPostConnectSteps
}, {
  key: 'inspect',
  nav_label: 'Inspect events',
  label: 'Inspect events sent to your CLI',
  description: "View the collection of the events that you've just triggered to your webhook producer. You can track events in both the Hookdeck Event Gateway dashboard and CLI.",
  icon: 'checklist' as const,
  Component: InspectEvents,
  reveal: revealGuidedCliOnboardingPostConnectSteps,
  full_width: true
}, {
  key: 'complete',
  label: "You're officially set up with the Hookdeck CLI",
  description: "You've completed everything necessary to receive webhooks locally. Now, inspect, debug, and retry webhooks right in your terminal, and explore more functionality in the Hookdeck Event Gateway dashboard.",
  icon: 'done_all' as const,
  Component: SetupComplete,
  reveal: revealGuidedCliOnboardingPostConnectSteps
}];
const GuidedCLIOnboarding: React.FC = () => {
  const $ = _c(37);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  const [selected_event_id, setSelectedEventId] = useState(null);
  const [completed_step, setCompletedStep] = useLocalStorage(`${team.id}:progress:guided_cli_onboarding_step`, steps[0].key);
  const {
    active_cli_sessions
  } = useContext(DashboardContext);
  const cli_connected = active_cli_sessions && active_cli_sessions.length > 0;
  const connect_cli_index = steps.findIndex(_temp);
  let t0;
  if ($[0] !== completed_step) {
    t0 = step_0 => step_0.key === completed_step;
    $[0] = completed_step;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const completed_to_index = steps.findIndex(t0);
  const post_connect_unlocked = connect_cli_index !== -1 && completed_to_index >= connect_cli_index;
  let t1;
  if ($[2] !== HookdeckAPI.sources) {
    t1 = () => HookdeckAPI.sources.list({
      limit: 1,
      disabled_at: null
    }).then(results => results.models.length > 0 ? results.models[0].authenticated ? HookdeckAPI.sources.get(results.models[0].id, {
      include: "config.auth"
    }) : results.models[0] : Promise.resolve(null));
    $[2] = HookdeckAPI.sources;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      refreshInterval: _temp2
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: source,
    mutate: mutateSources
  } = useSWR("guided_cli_onboarding_source", t1, t2);
  const context = {
    source: source || null,
    cli_connected,
    post_connect_unlocked,
    events_list: null,
    progress: 0,
    selected_event_id,
    setSelectedEventId
  };
  let progressed_to_index = steps.findIndex(step_1 => !step_1.reveal ? false : step_1.reveal(context) === false);
  if (progressed_to_index === -1) {
    progressed_to_index = steps.length - 1;
  } else {
    progressed_to_index = progressed_to_index - 1;
  }
  const progress = Math.min(progressed_to_index, completed_to_index + 1);
  context.progress = progress;
  let t3;
  if ($[5] !== progress) {
    t3 = steps.reduce((key, step_2, i) => {
      if (i <= progress && step_2.nav_label) {
        return step_2.key;
      }
      return key;
    }, steps[0].key);
    $[5] = progress;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const current_nav_step_key = t3;
  let t4;
  if ($[7] !== completed_step || $[8] !== progress) {
    t4 = {
      progress,
      steps,
      completion_milestone: completed_step
    };
    $[7] = completed_step;
    $[8] = progress;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    scroll_to_step_key
  } = useGuidedStepScroll(t4);
  const prev_tracked_completed_step_ref = useRef(null);
  let t5;
  if ($[10] !== HookdeckAPI.track || $[11] !== completed_step || $[12] !== progress) {
    t5 = () => {
      if (prev_tracked_completed_step_ref.current === null) {
        prev_tracked_completed_step_ref.current = completed_step;
        return;
      }
      if (prev_tracked_completed_step_ref.current === completed_step) {
        return;
      }
      prev_tracked_completed_step_ref.current = completed_step;
      const step_key = steps[progress]?.key;
      if (step_key) {
        HookdeckAPI.track.event("Completed CLI Onboarding Step", {
          step: step_key
        });
      }
    };
    $[10] = HookdeckAPI.track;
    $[11] = completed_step;
    $[12] = progress;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== completed_step || $[15] !== progress) {
    t6 = [completed_step, progress];
    $[14] = completed_step;
    $[15] = progress;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = steps.filter(_temp3);
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== current_nav_step_key) {
    t8 = <GuidedCLIOnboardingNav steps={t7 as {
      key: string;
      nav_label: string;
    }[]} current_step={current_nav_step_key} />;
    $[18] = current_nav_step_key;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const T0 = FullscreenLayout;
  const t9 = true;
  const t10 = true;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      t: 33
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const T1 = Container;
  const t12 = !selected_event_id;
  const t13 = !!selected_event_id;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      b: 14
    };
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Text size="2xl" bold={true} m={{
      b: 1
    }}>Test webhooks locally with the Hookdeck CLI</Text>;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Div m={t14}>{t15}<Div flex={{
        align: "center",
        gap: 1
      }}><Text muted={true}>Set up the Hookdeck CLI in minutes.</Text><Link neutral={true} href={LINKS.product_docs.cli} target="_blank" icon="link">For more detail, read our CLI docs</Link></Div></Div>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = steps.map((step_4, index) => {
    const block_step_interaction = progress < index && !!step_4.reveal && !step_4.reveal(context) && index > completed_to_index;
    return <Div key={step_4.key} flex={true} style={block_step_interaction ? {
      opacity: 0.2,
      pointerEvents: "none",
      position: "relative"
    } : {
      position: "relative"
    }}><span id={step_4.key} style={{
        position: "absolute",
        top: "-100px"
      }} /><StyledProgress is_last={steps.length - 1 === index} required={!step_4.optional}><Icon p={0.5} icon={step_4.icon as IconName} /></StyledProgress><Div p={{
        l: 12,
        b: 20
      }} flex={{
        grow: true
      }} w={{
        px: 998
      }}><Div w={step_4.full_width ? 100 : {
          px: GUIDED_CONNECTION_WIDTHS.step_container
        }}><Div><Text bold={true} size="l" flex={{
              align: "center"
            }}>{step_4.label}{step_4.optional && <Badge m={{
                l: 2
              }} neutral={true}>Optional</Badge>}</Text><Text as="p" muted={true} m={{
              b: 4
            }} max_w={{
              px: GUIDED_CONNECTION_WIDTHS.step_container
            }}>{step_4.description}</Text></Div>{step_4.Component && <step_4.Component context={context} is_current_step={progress === index} nextStep={() => {
            let target_index = index;
            while (target_index + 1 < steps.length && ((steps[target_index + 1] as {
              optional?: boolean;
            }).optional || (steps[target_index + 1] as {
              skip_over?: boolean;
            }).skip_over)) {
              const next_scroll_target_index = target_index + 2;
              if (next_scroll_target_index < steps.length) {
                const next_scroll_step = steps[next_scroll_target_index] as {
                  reveal?: (context: CLIOnboardingContext) => boolean;
                };
                const is_visible = !next_scroll_step.reveal || next_scroll_step.reveal(context) === true;
                if (!is_visible) {
                  break;
                }
              }
              target_index++;
            }
            if (index > completed_to_index) {
              setCompletedStep(steps[target_index].key);
            }
            const scroll_target_index = Math.min(target_index + 1, steps.length - 1);
            const scroll_target_key = steps[scroll_target_index].key;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                scroll_to_step_key(scroll_target_key);
              });
            });
          }} mutateContext={input => {
            if (input.source) {
              mutateSources(input.source);
            }
          }} />}</Div></Div></Div>;
  });
  let t18;
  if ($[24] !== T1 || $[25] !== t12 || $[26] !== t13 || $[27] !== t16 || $[28] !== t17) {
    t18 = <T1 medium={t12} xxlarge={t13}>{t16}{t17}</T1>;
    $[24] = T1;
    $[25] = t12;
    $[26] = t13;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== T0 || $[31] !== t11 || $[32] !== t18) {
    t19 = <T0 light={t9} no_decorations={t10} p={t11}>{t18}</T0>;
    $[30] = T0;
    $[31] = t11;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19 || $[35] !== t8) {
    t20 = <>{t8}{t19}</>;
    $[34] = t19;
    $[35] = t8;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  return t20;
};
export default GuidedCLIOnboarding;
function _temp(step) {
  return step.key === "connect_cli";
}
function _temp2(result) {
  return !result ? 5000 : 0;
}
function _temp3(step_3) {
  return !!step_3.nav_label;
}